/**
 * Pattern constants for block name validation.
 */

/**
 * Maximum allowed nesting depth for block layouts.
 *
 * Prevents stack overflow from deeply nested configurations. This limit is
 * enforced at layout validation time with a clear error message, and as a
 * defense-in-depth measure during ghost component rendering.
 *
 * A depth of 20 is generous for real-world use cases while protecting against
 * malicious or buggy configurations that could cause infinite recursion.
 */
export const MAX_LAYOUT_DEPTH = 20;

/**
 * Maximum allowed length for block names.
 *
 * Prevents potential memory and performance issues from extremely long names.
 * A limit of 100 characters is generous for real-world use cases while
 * protecting against malicious or buggy configurations.
 *
 * This applies to the full namespaced name (e.g., "theme:my-theme:my-block").
 */
export const MAX_BLOCK_NAME_LENGTH = 100;

/**
 * Symbol used to mark a block reference as optional and missing from the registry.
 * When this marker is returned from resolution functions, it signals that the block
 * should be silently skipped rather than throwing an error.
 *
 * Used in layout validation and block registration to identify optional
 * blocks that aren't registered and should be skipped during validation and rendering.
 */
export const OPTIONAL_MISSING = Symbol("optional-missing");

/**
 * Constants for failure type codes used by ghost blocks.
 *
 * These are internal codes for logic comparisons (e.g., determining which
 * section to show in the ghost tooltip). Display messages are generated
 * at render time in ghost-block.gjs based on these types.
 *
 * Custom failure reasons (like "hidden by priority" from the head block)
 * don't need a type constant - they're passed as display strings directly
 * via the `failureReason` field.
 */
export const FAILURE_TYPE = Object.freeze({
  /** Block's conditions evaluated to false. */
  CONDITION_FAILED: "condition-failed",
  /** Container block has no children that passed their conditions. */
  NO_VISIBLE_CHILDREN: "no-visible-children",
  /** Block reference uses `?` suffix but isn't registered. */
  OPTIONAL_MISSING: "optional-missing",
});

/**
 * A resolved-block marker indicating an optional block reference (`name?`)
 * that isn't registered. Returned by resolution functions such as
 * `tryResolveBlock()` in place of a block class.
 */
export interface OptionalMissingMarker {
  optionalMissing: typeof OPTIONAL_MISSING;
  name: string;
}

/**
 * Checks if a resolved block is an optional missing block marker.
 *
 * @param resolvedBlock - The result from tryResolveBlock.
 * @returns True if the block is an optional missing marker.
 */
export function isOptionalMissing(
  resolvedBlock: unknown
): resolvedBlock is OptionalMissingMarker {
  return (
    typeof resolvedBlock === "object" &&
    resolvedBlock !== null &&
    "optionalMissing" in resolvedBlock &&
    resolvedBlock.optionalMissing === OPTIONAL_MISSING
  );
}

/**
 * Valid block name pattern: lowercase letters, numbers, and hyphens.
 * Must start with a letter. Examples: "hero-banner", "sidebar-blocks", "my-block-1"
 *
 * Used for both block names and outlet names since they follow the same format.
 */
export const VALID_BLOCK_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;

/**
 * Valid block ID pattern for the `id` entry property.
 * Same format as block names: lowercase letters, numbers, and hyphens,
 * must start with a letter.
 *
 * Examples: "featured-banner", "sidebar-1", "main-content"
 */
export const VALID_BLOCK_ID_PATTERN = VALID_BLOCK_NAME_PATTERN;

/**
 * Valid namespaced block name pattern. Supports three formats:
 *
 * - **Core blocks**: `block-name` (no prefix)
 * - **Plugin blocks**: `plugin-name:block-name` (where plugin-name is not "theme")
 * - **Theme blocks**: `theme:theme-name:block-name`
 *
 * Each segment must start with a letter and contain only lowercase letters,
 * numbers, and hyphens.
 *
 * @example
 * ```
 * // Valid patterns:
 * "group"                      // Core block
 * "chat:message-widget"        // Plugin block
 * "theme:tactile:hero-banner"  // Theme block
 *
 * // Invalid patterns:
 * "Theme:Name:block"           // Uppercase not allowed
 * "theme:block"                // Theme requires namespace segment
 * "my_block"                   // Underscores not allowed
 * ```
 */
export const VALID_NAMESPACED_BLOCK_PATTERN =
  /^(?:theme:[a-z][a-z0-9-]*:[a-z][a-z0-9-]*|(?!theme:)[a-z][a-z0-9-]*:[a-z][a-z0-9-]*|[a-z][a-z0-9-]*)$/;

/**
 * The parsed components of a block name.
 */
export interface ParsedBlockName {
  type: "core" | "plugin" | "theme";
  namespace: string | null;
  name: string;
}

/**
 * Parses a full block name into its components.
 *
 * @param fullName - The full block name.
 * @returns An object with the parsed components, or `null` if the name is invalid.
 *
 * @example
 * ```
 * parseBlockName("group")
 * // => { type: "core", namespace: null, name: "group" }
 *
 * parseBlockName("chat:message-widget")
 * // => { type: "plugin", namespace: "chat", name: "message-widget" }
 *
 * parseBlockName("theme:tactile:hero-banner")
 * // => { type: "theme", namespace: "theme:tactile", name: "hero-banner" }
 *
 * parseBlockName("Invalid_Name")
 * // => null
 * ```
 */
export function parseBlockName(fullName: string): ParsedBlockName | null {
  // Theme: theme:namespace:name
  const themeMatch = fullName.match(
    /^theme:([a-z][a-z0-9-]*):([a-z][a-z0-9-]*)$/
  );
  if (themeMatch) {
    return {
      type: "theme",
      namespace: `theme:${themeMatch[1]}`,
      name: themeMatch[2],
    };
  }

  // Plugin: namespace:name (where namespace is NOT "theme")
  const pluginMatch = fullName.match(/^([a-z][a-z0-9-]*):([a-z][a-z0-9-]*)$/);
  if (pluginMatch && pluginMatch[1] !== "theme") {
    return { type: "plugin", namespace: pluginMatch[1], name: pluginMatch[2] };
  }

  // Core: just name
  const coreMatch = fullName.match(/^[a-z][a-z0-9-]*$/);
  if (coreMatch) {
    return { type: "core", namespace: null, name: fullName };
  }

  return null;
}

/**
 * The result of parsing a block reference string.
 */
export interface ParsedBlockReference<Name = string | object> {
  name: Name;
  optional: boolean;
}

/**
 * Parses a block reference string to extract the block name and optional flag.
 *
 * Block references can be marked as optional by appending a `?` suffix to the
 * name. Optional blocks that are not registered will be silently skipped
 * instead of throwing an error.
 *
 * Supports all namespaced formats:
 * - Core: `"block-name"` or `"block-name?"`
 * - Plugin: `"plugin:block"` or `"plugin:block?"`
 * - Theme: `"theme:namespace:block"` or `"theme:namespace:block?"`
 *
 * @param blockRef - The block reference. If a string, may have an optional `?`
 *   suffix. Non-string references (e.g., component classes) are returned as-is
 *   in the `name` property with `optional: false`.
 * @returns Parsed result with the clean block name (or original reference) and
 *   whether it's optional.
 *
 * @example
 * ```
 * parseBlockReference("chat:widget?")
 * // => { name: "chat:widget", optional: true }
 *
 * parseBlockReference("hero-banner")
 * // => { name: "hero-banner", optional: false }
 * ```
 */
export function parseBlockReference(
  blockRef: string
): ParsedBlockReference<string>;
export function parseBlockReference<T extends object>(
  blockRef: T
): ParsedBlockReference<T>;
export function parseBlockReference(
  blockRef: string | object
): ParsedBlockReference {
  if (typeof blockRef === "string" && blockRef.endsWith("?")) {
    return { name: blockRef.slice(0, -1), optional: true };
  }
  return { name: blockRef, optional: false };
}
